/* ============================================================
   navbar-ecoleap
   - Desktop: details/summary hover dropdown with ecoleap-style
     slide-from-behind animation + keyboard support.
   - Mobile: slide-in submenu overlay panels inside offcanvas.
   Plain JS, no BS collapse dependency. Editor-friendly.
   ============================================================ */
(function () {
  'use strict';

  var CLOSE_DELAY = 0;
  var DROPDOWN_HIDE_DELAY = 600;
  var DROPDOWN_CONTENT_DELAY = 100;

  function syncDesktopHeaderHeight(root) {
    if (!root) return;
    var height = root.getBoundingClientRect().height;
    if (height > 0) root.style.setProperty('--ecoleap-header-height', height.toFixed(1) + 'px');
  }

  function updateBodyDropdownState() {
    var hasOpen = document.querySelector('.navbar-ecoleap-desktop details > summary[open]');
    document.body.classList.toggle('has-dropdown-menu', Boolean(hasOpen));
  }

  function bindDetailsDropdown(details) {
    if (!details || details.dataset.ecoleapBound === '1') return;
    details.dataset.ecoleapBound = '1';

    var closeTimer = null;
    var contentTimer = null;
    var hideTimer = null;
    var summary = details.querySelector('summary');
    var dropdown = details.querySelector('.dropdown');

    var clearTimer = function (timer) {
      if (timer) window.clearTimeout(timer);
    };

    var clearTimers = function () {
      clearTimer(closeTimer);
      clearTimer(contentTimer);
      clearTimer(hideTimer);
      closeTimer = null;
      contentTimer = null;
      hideTimer = null;
    };

    var updateAria = function (expanded) {
      if (summary) summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    var open = function () {
      clearTimers();
      syncDesktopHeaderHeight(details.closest('.navbar-ecoleap-desktop'));
      details.setAttribute('open', '');
      if (summary) summary.setAttribute('open', '');
      updateAria(true);
      updateBodyDropdownState();
      contentTimer = window.setTimeout(function () {
        if (dropdown) {
          dropdown.setAttribute('open', '');
          dropdown.classList.add('is-open');
        }
        contentTimer = null;
      }, DROPDOWN_CONTENT_DELAY);
    };

    var close = function (delay) {
      clearTimer(closeTimer);
      clearTimer(contentTimer);
      clearTimer(hideTimer);
      contentTimer = null;
      hideTimer = null;
      closeTimer = window.setTimeout(function () {
        if (summary) summary.removeAttribute('open');
        if (dropdown) {
          dropdown.removeAttribute('open');
          dropdown.classList.remove('is-open');
        }
        updateAria(false);
        updateBodyDropdownState();
        hideTimer = window.setTimeout(function () {
          details.removeAttribute('open');
          updateBodyDropdownState();
          hideTimer = null;
        }, DROPDOWN_HIDE_DELAY);
        closeTimer = null;
      }, typeof delay === 'number' ? delay : CLOSE_DELAY);
    };

    details.addEventListener('mouseenter', open);
    details.addEventListener('mouseleave', function () { close(CLOSE_DELAY); });

    details.addEventListener('focusin', open);
    details.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!details.contains(document.activeElement)) close(CLOSE_DELAY);
      }, 0);
    });

    details.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && details.hasAttribute('open')) {
        e.preventDefault();
        clearTimers();
        close(0);
        if (summary) summary.focus();
      }
    });

    if (summary) {
      summary.addEventListener('click', function (e) {
        if (window.matchMedia('(hover: none)').matches) {
          e.preventDefault();
          if (dropdown && dropdown.hasAttribute('open')) {
            close(0);
          } else {
            open();
          }
        } else {
          e.preventDefault();
          var link = summary.getAttribute('data-link');
          if (link) window.location.href = link;
        }
      });
    }
  }

  function bindDesktop(root) {
    syncDesktopHeaderHeight(root);
    if (root && root.dataset.ecoleapHeightBound !== '1') {
      root.dataset.ecoleapHeightBound = '1';
      window.addEventListener('resize', function () {
        syncDesktopHeaderHeight(root);
      });
    }
    root.querySelectorAll('details[trigger="hover"]').forEach(bindDetailsDropdown);
  }

  function bindMobile(drawer) {
    if (!drawer || drawer.dataset.ecoleapBound === '1') return;
    drawer.dataset.ecoleapBound = '1';

    // Ensure the Bootstrap Offcanvas instance is initialized early so that
    // delegated clicks (including those re-dispatched by analytics/event
    // collectors) can reuse it instead of racing through the constructor.
    if (window.bootstrap && bootstrap.Offcanvas) {
      bootstrap.Offcanvas.getOrCreateInstance(drawer);
    }

    var root = drawer.querySelector('.m-level-root');
    var panels = drawer.querySelectorAll('.m-panel');
    var panelTriggers = drawer.querySelectorAll('[data-open-panel]');
    var subgroupTriggers = drawer.querySelectorAll('[data-toggle-subgroup]');
    var nestedLists = drawer.querySelectorAll('.m-sublist-nested');

    var openPanel = function (id) {
      panels.forEach(function (p) {
        var isCurrent = p.id === id;
        p.classList.toggle('is-active', isCurrent);
        p.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      });
      panelTriggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', btn.getAttribute('data-open-panel') === id ? 'true' : 'false');
      });
      if (root) root.classList.add('is-pushed');
      drawer.scrollTop = 0;
    };

    var closePanels = function () {
      panels.forEach(function (p) {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      });
      panelTriggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      subgroupTriggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      nestedLists.forEach(function (list) {
        list.classList.remove('is-open');
      });
      if (root) root.classList.remove('is-pushed');
    };

    panelTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        openPanel(btn.getAttribute('data-open-panel'));
      });
    });

    drawer.querySelectorAll('[data-close-panel]').forEach(function (btn) {
      btn.addEventListener('click', closePanels);
    });

    subgroupTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var targetId = btn.getAttribute('data-toggle-subgroup');
        var target = targetId ? drawer.querySelector('#' + targetId) : null;
        var isExpanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isExpanded));
        if (target) target.classList.toggle('is-open', !isExpanded);
      });
    });

    drawer.addEventListener('show.bs.offcanvas', closePanels);
    drawer.addEventListener('hidden.bs.offcanvas', closePanels);
  }

  function init(context) {
    var scope = context || document;
    scope.querySelectorAll('.navbar-ecoleap-desktop').forEach(bindDesktop);
    var drawer = scope.querySelector('#offcanvas-menu-ecoleap');
    if (drawer) bindMobile(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
